import React, { useState } from 'react';
import { useApp } from '../context/AppContext.tsx';
import { HomepageSectionConfig } from '../types.ts';
import HeroHeadline from '../components/home/HeroHeadline.tsx';
import WhyChooseUsGrid from '../components/home/WhyChooseUsGrid.tsx';
import QuickStats from '../components/home/QuickStats.tsx';
import FeaturedProducts from '../components/home/FeaturedProducts.tsx';
import PortfolioGrid from '../components/home/PortfolioGrid.tsx';
import ArticleGrid from '../components/home/ArticleGrid.tsx';
import GalleryVideoWidget from '../components/home/GalleryVideoWidget.tsx';
import TestimonialsSection from '../components/home/TestimonialsSection.tsx';
import AdBannerSlot from '../components/home/AdBannerSlot.tsx';
import SoftSellingCTA from '../components/home/SoftSellingCTA.tsx';
import PersonnelTeamSection from '../components/home/PersonnelTeamSection.tsx';
import MediaShowcaseSection from '../components/home/MediaShowcaseSection.tsx';
import DirectSectionEditModal from '../components/home/DirectSectionEditModal.tsx';
import { Edit3, LayoutDashboard, Sliders } from 'lucide-react';

export default function Home() {
  const { settings, user, navigate } = useApp();

  const [activeEditingSection, setActiveEditingSection] = useState<HomepageSectionConfig | null>(null);

  React.useEffect(() => {
    const customTab = settings?.pageTabTitles?.home?.trim();
    if (customTab) {
      document.title = customTab;
    } else {
      const sName = (settings?.siteName || '').trim();
      const tLine = (settings?.tagline || '').trim();
      const dynamicTitle = (settings?.seo?.metaTitle?.trim()) || (tLine && sName ? `${sName} - ${tLine}` : (sName || 'Beranda'));
      document.title = dynamicTitle;
    }
  }, [settings?.pageTabTitles?.home, settings?.seo?.metaTitle, settings?.siteName, settings?.tagline]);

  // Ensure all registered grid types exist in homepage configuration
  const ensureAllSections = (rawSections: HomepageSectionConfig[]): HomepageSectionConfig[] => {
    const list = [...(rawSections || [])];
    const hasPortfolio1 = list.some((s) => s.id === 'sec-portfolio' || (s.type === 'portfolio_grid' && s.id !== 'sec-portfolio-2'));
    if (!hasPortfolio1) {
      list.push({
        id: 'sec-portfolio',
        type: 'portfolio_grid',
        badge: 'PROFIL PROJECT UNGGULAN',
        title: 'Profil Project & Rekam Jejak Unggulan',
        subtitle: 'Dokumentasi implementasi proyek nyata, konsultasi keberlanjutan, dan hasil kerja sama strategis dengan mitra.',
        enabled: true,
        order: 5,
        layoutStyle: 'cards',
        itemLimit: 1,
        buttonText: 'Selengkapnya →',
        buttonEnabled: true,
      });
    }



    if (!list.some((s) => s.type === 'personnel_team')) {
      list.push({
        id: 'sec-personnel',
        type: 'personnel_team',
        badge: 'EXPERT ADVISORY & RESEARCH TEAM',
        title: 'Distinguished Faculty, Advisors & Climate Fellows',
        subtitle: 'Supported by academic researchers from Institut Teknologi Bandung and certified environmental engineers dedicated to corporate sustainability excellence.',
        enabled: true,
        order: 9,
      });
    }

    if (!list.some((s) => s.type === 'media_showcase')) {
      list.push({
        id: 'sec-media-showcase',
        type: 'media_showcase',
        badge: 'VISUAL SPOTLIGHT & INNOVATION',
        title: 'Media & Visual Highlights',
        subtitle: 'Showcasing clean technology deployments, research symposiums, and sustainable enterprise innovation projects.',
        enabled: true,
        order: 10,
        buttonText: 'Lihat Semua Sorotan Media →',
        buttonEnabled: true,
      });
    }

    // Sort by order and return strictly sequential orders
    return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((sec, i) => ({
      ...sec,
      order: i + 1,
    }));
  };

  // Get active sorted sections
  const sections = ensureAllSections(settings?.sections || [])
    .filter((sec) => sec.enabled)
    .sort((a, b) => a.order - b.order);

  const handleNavigateToDashboard = (type: string) => {
    let tabId = type;
    if (type === 'quick_stats' || type === 'softselling_cta') {
      tabId = 'frontend_customizer';
    } else if (type === 'hero_headline' || type === 'why_choose_us' || type === 'testimonials') {
      tabId = 'why_choose_us';
    } else if (type === 'featured_products') {
      tabId = 'products';
    } else if (type === 'articles_blog_grid') {
      tabId = 'articles';
    } else if (type === 'portfolio_grid') {
      tabId = 'portfolio';
    } else if (type === 'gallery_video') {
      tabId = 'gallery';
    } else if (type === 'personnel_team' || type === 'media_showcase') {
      tabId = 'sections';
    }
    try {
      localStorage.setItem('admin_target_tab', tabId);
    } catch (e) {
      // fallback
    }
    navigate('admin');
  };

  const getSectionLabel = (type: string): string => {
    switch (type) {
      case 'hero_headline':
        return 'Hero Headline Banner';
      case 'why_choose_us':
        return 'Mengapa Memilih ALY LAW FIRM';
      case 'quick_stats':
        return 'Quick Impact Statistics';
      case 'featured_products':
        return 'Services & Solutions';
      case 'portfolio_grid':
        return 'Profil Project & Rekam Jejak Unggulan';
      case 'articles_blog_grid':
        return 'Articles & Insights';
      case 'gallery_video':
        return 'Media & Video Gallery';
      case 'testimonials':
        return 'Enterprise Testimonials';
      case 'ad_banner':
        return 'Sponsor & Partner Banner';
      case 'softselling_cta':
        return 'Soft-Selling CTA Banner';
      case 'personnel_team':
        return 'Personnel & Advisory Board';
      case 'media_showcase':
        return 'Visual Highlights & Media';
      default:
        return 'Homepage Section';
    }
  };

  const renderSectionComponent = (sec: HomepageSectionConfig) => {
    switch (sec.type) {
      case 'hero_headline':
        return <HeroHeadline key={sec.id} />;
      case 'why_choose_us':
        return <WhyChooseUsGrid key={sec.id} badge={sec.badge} title={sec.title} subtitle={sec.subtitle} />;
      case 'quick_stats':
        return <QuickStats key={sec.id} />;
      case 'featured_products':
        return (
          <FeaturedProducts
            key={sec.id}
            badge={sec.badge}
            title={sec.title}
            subtitle={sec.subtitle}
            buttonText={sec.buttonText}
            buttonEnabled={sec.buttonEnabled}
          />
        );
      case 'portfolio_grid':
        return (
          <PortfolioGrid
            key={sec.id}
            badge={sec.badge}
            title={sec.title}
            subtitle={sec.subtitle}
            buttonText={sec.buttonText}
            buttonEnabled={sec.buttonEnabled}
            layoutStyle={sec.layoutStyle}
            limit={sec.itemLimit ?? 1}
            sectionId={sec.id}
            selectedPortfolioId={sec.selectedPortfolioId}
          />
        );
      case 'articles_blog_grid':
        return (
          <ArticleGrid
            key={sec.id}
            badge={sec.badge}
            title={sec.title}
            subtitle={sec.subtitle}
            buttonText={sec.buttonText}
            buttonEnabled={sec.buttonEnabled}
            showSidebar={true}
          />
        );
      case 'gallery_video':
        return (
          <GalleryVideoWidget
            key={sec.id}
            badge={sec.badge}
            title={sec.title}
            subtitle={sec.subtitle}
            buttonText={sec.buttonText}
            buttonEnabled={sec.buttonEnabled}
          />
        );
      case 'personnel_team':
        return <PersonnelTeamSection key={sec.id} badge={sec.badge} title={sec.title} subtitle={sec.subtitle} />;
      case 'media_showcase':
        return (
          <MediaShowcaseSection
            key={sec.id}
            badge={sec.badge}
            title={sec.title}
            subtitle={sec.subtitle}
            buttonText={sec.buttonText}
            buttonEnabled={sec.buttonEnabled}
          />
        );
      case 'testimonials':
        return <TestimonialsSection key={sec.id} badge={sec.badge} title={sec.title} subtitle={sec.subtitle} />;
      case 'ad_banner':
        return <AdBannerSlot key={sec.id} position="bottom" />;
      case 'softselling_cta':
        return <SoftSellingCTA key={sec.id} badge={sec.badge} title={sec.title} subtitle={sec.subtitle} />;
      default:
        return null;
    }
  };

  return (
    <div id="homepage-container" className="w-full pb-12">
      
      {/* Admin Floating Banner Bar if user is logged in */}
      {user && (
        <div className="bg-slate-900 text-white px-4 py-2.5 shadow-md flex items-center justify-between gap-3 text-xs border-b border-amber-500/30 sticky top-0 z-30 font-sans">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span className="font-extrabold text-amber-300">Homepage Admin Mode Active</span>
            <span className="hidden sm:inline text-slate-300">
              — Hover or click <strong className="text-white">"Edit Section"</strong> to customize section text & media directly.
            </span>
          </div>

          <button
            type="button"
            onClick={() => handleNavigateToDashboard('sections')}
            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-lg text-[11px] flex items-center gap-1.5 transition-all cursor-pointer shrink-0 shadow-xs"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span>Layout in Dashboard</span>
          </button>
        </div>
      )}

      {/* Render Sections with Direct Edit Controls */}
      {sections.map((sec) => (
        <div key={sec.id} className="relative group/section">
          
          {/* Admin Control Overlay Top Bar per Section */}
          {user && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-3 pb-1">
              <div className="bg-slate-900/90 backdrop-blur-xs text-white rounded-2xl p-2 px-3 border border-slate-700 shadow-lg flex flex-wrap items-center justify-between gap-2 text-xs font-sans">
                
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-emerald-600 text-white font-black rounded-md text-[10px] uppercase tracking-wider">
                    {getSectionLabel(sec.type)}
                  </span>
                  <span className="text-slate-300 font-semibold hidden md:inline truncate max-w-xs">
                    {sec.title || sec.id}
                  </span>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveEditingSection(sec)}
                    className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold rounded-xl text-xs flex items-center gap-1.5 shadow-sm transition-transform active:scale-95 cursor-pointer"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Edit Section</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleNavigateToDashboard(sec.type)}
                    className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-xs flex items-center gap-1 transition-colors cursor-pointer border border-slate-700"
                  >
                    <LayoutDashboard className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="hidden sm:inline">To Dashboard</span>
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* Actual Homepage Section */}
          {renderSectionComponent(sec)}
        </div>
      ))}

      {/* Direct Section Editor Modal */}
      {activeEditingSection && (
        <DirectSectionEditModal
          section={activeEditingSection}
          isOpen={!!activeEditingSection}
          onClose={() => setActiveEditingSection(null)}
          onNavigateToDashboard={handleNavigateToDashboard}
        />
      )}

    </div>
  );
}
